import { serializeScriptFallbacks } from "../script-fallbacks";
import { escapeHtml } from "./escape";
import { buildPagedPreviewBootstrapScript } from "./paged-preview-bootstrap";
import { buildPrintDocumentCss } from "./print-document-css";
import {
  defaultPreviewSurfaceBackground,
  pagedjsScriptHref,
  pagedPreviewFallbackEvent,
  type PrintDocumentInput,
} from "./types";

function fnv1a36(material: string) {
  // FNV-1a 32-bit — short, deterministic, good enough for preview identity.
  let hash = 0x811c9dc5;
  for (let index = 0; index < material.length; index += 1) {
    hash ^= material.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

/**
 * Shell identity for warm preview iframes. Changing this forces a full srcDoc
 * reload (Paged.js + font faces). Content/options alone stay warm.
 */
export function buildPreviewShellKey(
  fontFamily: PrintDocumentInput["options"]["fontFamily"],
  scriptFallbacks: PrintDocumentInput["scriptFallbacks"] = [],
  externalFontHref = "",
) {
  return fnv1a36(
    [
      fontFamily,
      serializeScriptFallbacks(scriptFallbacks ?? []),
      externalFontHref,
    ].join("\u0001"),
  );
}

/** Stable content id so the host only promotes an iframe for this preview. */
export function buildPreviewRevision(
  bodyHtml: string,
  options: PrintDocumentInput["options"],
  externalFontHref?: string,
  previewSurfaceBackground?: string,
  scriptFallbacks: PrintDocumentInput["scriptFallbacks"] = [],
  dir: PrintDocumentInput["dir"] = "ltr",
  lang: PrintDocumentInput["lang"] = "en",
) {
  return fnv1a36(
    [
      bodyHtml,
      options.paperSize,
      options.orientation,
      options.margins,
      options.theme,
      options.codeTheme,
      options.diagramTheme,
      String(options.pageNumbers),
      options.fontFamily,
      String(options.fontSize),
      options.diagramFontFamily,
      String(options.diagramFontSize),
      options.codeFontFamily,
      String(options.codeFontSize),
      externalFontHref ?? "",
      previewSurfaceBackground ?? "",
      serializeScriptFallbacks(scriptFallbacks ?? []),
      dir ?? "ltr",
      lang ?? "en",
    ].join("\u0001"),
  );
}

export function readPreviewRevisionFromSrcDoc(srcDoc: string) {
  const match = /data-preview-revision="([^"]+)"/.exec(srcDoc);
  return match?.[1] ?? null;
}

export function buildPrintDocumentHtml({
  title,
  bodyHtml,
  options,
  variant = "pdf",
  embeddedFontCss,
  externalFontHref,
  scriptFallbacks = [],
  dir = "ltr",
  lang = "en",
  previewSurfaceBackground = defaultPreviewSurfaceBackground,
}: PrintDocumentInput) {
  const documentDir = dir === "rtl" ? "rtl" : "ltr";
  const documentLang = lang && lang.trim().length > 0 ? lang.trim() : "en";
  const isPreview = variant === "preview";
  // Keep @font-face out of Paged.js removeStyles/polisher so chunking uses the
  // same loaded faces as document.fonts.ready (no tear-down/reload race).
  const fontLink = externalFontHref
    ? `<link rel="stylesheet" href="${escapeHtml(externalFontHref)}" data-pagedjs-ignore>`
    : "";
  const embeddedFontStyle = embeddedFontCss
    ? `<style data-pagedjs-ignore>\n${embeddedFontCss}\n</style>`
    : "";
  const styleContent = buildPrintDocumentCss(
    options,
    previewSurfaceBackground,
    {
      // Both preview and PDF use Paged.js margin boxes for page numbers.
      includePagedPageNumbers: options.pageNumbers,
      includePreviewSheetChrome: isPreview,
      scriptFallbacks,
    },
  );
  const previewVisibilityGate = isPreview
    ? `<style data-pagedjs-ignore>
@media screen {
  /* Paged.js measures the source document before it creates page sheets.
     Keep that source in layout so pagination remains accurate, but prevent it
     from flashing before the pagination callback has finished. */
  html:not([data-paged-status="complete"]):not([data-paged-status="fallback"])
    body {
    visibility: hidden !important;
  }
}
</style>`
    : "";
  // Preview and PDF both paginate with Paged.js under the same @page rules.
  // Preview: auto=false + onload bootstrap so the shell can warm-repaginate.
  // PDF: auto=true one-shot (unchanged).
  const pagedScriptOnLoad = isPreview
    ? `onload="if(window.__markdown2pdfBootstrapPaged){window.__markdown2pdfBootstrapPaged()}"`
    : "";
  const pagedScripts = [
    buildPagedPreviewBootstrapScript(
      previewSurfaceBackground,
      options,
      variant,
      scriptFallbacks,
    ),
    `<script src="${escapeHtml(pagedjsScriptHref)}" ${pagedScriptOnLoad} onerror="document.documentElement.dataset.pagedStatus='fallback';document.dispatchEvent(new CustomEvent('${pagedPreviewFallbackEvent}'))"></script>`,
  ].join("\n");
  const previewRevision = isPreview
    ? buildPreviewRevision(
        bodyHtml,
        options,
        externalFontHref,
        previewSurfaceBackground,
        scriptFallbacks,
        documentDir,
        documentLang,
      )
    : null;
  const htmlAttrs = [
    `lang="${escapeHtml(documentLang)}"`,
    // Paged.js overflow/break math assumes LTR source flow. Keep the root and
    // <main> LTR for measurement; Arabic writing direction is restored on
    // paginated page content via data-text-dir CSS.
    'dir="ltr"',
    `data-text-dir="${documentDir}"`,
  ];
  if (previewRevision) {
    htmlAttrs.push(
      `data-preview-revision="${escapeHtml(previewRevision)}"`,
    );
  }
  const htmlOpen = `<html ${htmlAttrs.join(" ")}>`;

  return [
    "<!doctype html>",
    htmlOpen,
    "<head>",
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${escapeHtml(title)}</title>`,
    fontLink,
    embeddedFontStyle,
    '<style id="markdown2pdf-print-css">',
    styleContent,
    "</style>",
    previewVisibilityGate,
    "</head>",
    [
      `<body data-paper-size="${options.paperSize}"`,
      `data-orientation="${options.orientation}"`,
      `data-margin-preset="${options.margins}"`,
      `data-theme="${options.theme}"`,
      `data-code-theme="${options.codeTheme}"`,
      `data-diagram-theme="${options.diagramTheme}"`,
      `data-page-numbers="${String(options.pageNumbers)}"`,
      `data-font-family="${options.fontFamily}"`,
      `data-font-size="${options.fontSize}"`,
      `data-diagram-font-family="${options.diagramFontFamily}"`,
      `data-diagram-font-size="${options.diagramFontSize}"`,
      `data-code-font-family="${options.codeFontFamily}"`,
      `data-code-font-size="${options.codeFontSize}"`,
      `data-print-variant="${variant}">`,
    ].join(" "),
    '<main class="pdf-document markdown-preview" aria-label="PDF document content">',
    bodyHtml,
    "</main>",
    pagedScripts,
    "</body>",
    "</html>",
  ].join("\n");
}
